import React from 'react';
import PropTypes from 'prop-types';
import Heading from '../../atoms/Heading';
import ToggleSwitch from '../../atoms/ToggleSwitch';
import NumberInput from '../../atoms/NumberInput/NumberInput';
import Select from '../../atoms/Select/Select';
import { getFaultName, FaultCodes } from '@feedflo/fault-codes';
import {
  ALERTS_WITH_NO_FEED_TIME,
  EDITOR_NO_FEED_TIMING_LABELS,
  formatAlertThresholdSummary,
  formatReminderSummary,
  formatResolvedSummary,
  getEditorDurationTimingLabel,
  isImmediateOneShotAlert,
} from '../../utils/tierNotificationSettings';
import './TierSettingsPanel.scss';

const FAULT_NAME_OVERRIDES = {
  [FaultCodes.BIN_CONTROLLER_SLIDE_OPEN]: 'Bin Switch (Bin Operator)',
  [FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN]: 'Hard Bin Lockdown (Bin Operator)',
};

function getTierFaultName(faultCode) {
  return FAULT_NAME_OVERRIDES[faultCode] ?? getFaultName(faultCode);
}

const NO_FEED_ALERT_OPTIONS = [
  {
    id: 'before_empty',
    name: EDITOR_NO_FEED_TIMING_LABELS.before_empty,
    description: 'Warning before feeders run empty',
  },
  {
    id: 'after_empty',
    name: EDITOR_NO_FEED_TIMING_LABELS.after_empty,
    description: 'Alert after period of no feeding',
  },
];

function formatAlertSummary(alertSettings, faultCode) {
  return formatAlertThresholdSummary({
    faultCode,
    hours: alertSettings.hours,
    thresholdType: alertSettings.type,
  });
}

function TierSettingsSummary({ tierName, settings, showTitle }) {
  const enabledSettings = Object.entries(settings).filter(([, faultSettings]) => faultSettings.alert?.enabled);

  return (
    <div className="TierSettingsPanel TierSettingsPanel--readOnly">
      {showTitle && <Heading className="TierSettingsPanel-title">{tierName}</Heading>}
      {enabledSettings.length === 0 ? (
        <div className="TierSettingsPanel-summaryEmpty">No alerts enabled</div>
      ) : (
        <div className="TierSettingsPanel-summaryList">
          {enabledSettings.map(([faultType, faultSettings]) => {
            const faultName = getTierFaultName(parseInt(faultType));
            const alertSettings = faultSettings.alert || {};
            const reminderSettings = faultSettings.reminders || {};
            const resolvedSettings = faultSettings.resolved || {};
            const faultCode = parseInt(faultType);
            const isImmediateOneShot = isImmediateOneShotAlert(faultCode);
            const alertText = formatAlertSummary(alertSettings, faultCode);
            const reminderText =
              !isImmediateOneShot &&
              reminderSettings.enabled &&
              reminderSettings.hours > 0
                ? formatReminderSummary(reminderSettings.hours)
                : '';
            const resolvedText =
              !isImmediateOneShot && resolvedSettings.enabled
                ? formatResolvedSummary(faultCode)
                : '';

            return (
              <div key={faultType} className="TierSettingsPanel-summaryItem">
                <strong>{faultName}</strong>: {alertText}
                {reminderText}
                {resolvedText}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

TierSettingsSummary.propTypes = {
  tierName: PropTypes.string,
  settings: PropTypes.object,
  showTitle: PropTypes.bool,
};

function TierSettingsPanel({
  tierName = 'No Tier Name',
  settings = {},
  onSettingChange = () => {},
  disabled = false,
  showTitle = true,
}) {
  const currentSettings = settings;
  const handleToggle = (faultType) => {
    if (disabled) return;

    const faultCode = parseInt(faultType);
    const isImmediateOneShot = isImmediateOneShotAlert(faultCode);

    const newSettings = {
      ...currentSettings,
      [faultType]: {
        ...currentSettings[faultType],
        alert: {
          ...currentSettings[faultType]?.alert,
          enabled: !currentSettings[faultType]?.alert.enabled,
          ...(isImmediateOneShot
            ? {
                hours: 0,
                type: 'duration',
                option: 'after_duration',
              }
            : {}),
        },
        ...(isImmediateOneShot
          ? {
              reminders: {
                enabled: false,
                hours: 0,
              },
              resolved: {
                enabled: false,
              },
            }
          : {}),
      },
    };
    onSettingChange(newSettings);
  };

  const handleTypeChange = (faultType, alertType) => {
    if (disabled) return;

    const nofeed_types = NO_FEED_ALERT_OPTIONS.map((option) => option.id);
    const newType = nofeed_types.includes(alertType) ? 'nofeed_time' : 'duration';
    const newHours =
      alertType === 'before_empty'
        ? -Math.abs(currentSettings[faultType]?.alert?.hours)
        : Math.abs(currentSettings[faultType]?.alert?.hours);

    const newSettings = {
      ...currentSettings,
      [faultType]: {
        ...currentSettings[faultType],
        alert: {
          ...currentSettings[faultType]?.alert,
          type: newType,
          hours: newHours,
          option: alertType,
        },
      },
    };
    onSettingChange(newSettings);
  };

  const handleHoursChange = (faultType, hours) => {
    if (disabled) return;

    // Allow empty string during editing - set to null so input can be cleared
    if (hours === '' || hours === null || hours === undefined) {
      const newSettings = {
        ...currentSettings,
        [faultType]: {
          ...currentSettings[faultType],
          alert: {
            ...currentSettings[faultType]?.alert,
            hours: null,
          },
        },
      };
      onSettingChange(newSettings);
      return;
    }

    const shouldBeNegative = currentSettings[faultType]?.alert?.option === 'before_empty';
    const parsedHours = parseFloat(hours);
    if (isNaN(parsedHours)) return;

    const newHours = shouldBeNegative ? -Math.abs(parsedHours) : Math.abs(parsedHours);

    const newSettings = {
      ...currentSettings,
      [faultType]: {
        ...currentSettings[faultType],
        alert: {
          ...currentSettings[faultType]?.alert,
          hours: newHours,
        },
      },
    };
    onSettingChange(newSettings);
  };

  const handleHoursBlur = (faultType, hours) => {
    if (disabled) return;

    // Convert emptyish values to 0 on blur
    const trimmedHours = typeof hours === 'string' ? hours.trim() : hours;
    if (trimmedHours === '' || trimmedHours === null || trimmedHours === undefined) {
      const newSettings = {
        ...currentSettings,
        [faultType]: {
          ...currentSettings[faultType],
          alert: {
            ...currentSettings[faultType]?.alert,
            hours: 0,
          },
        },
      };
      onSettingChange(newSettings);
      return;
    }

    const shouldBeNegative = currentSettings[faultType]?.alert?.option === 'before_empty';
    const parsedHours = parseFloat(trimmedHours);
    if (isNaN(parsedHours)) {
      // If invalid, reset to 0
      const newSettings = {
        ...currentSettings,
        [faultType]: {
          ...currentSettings[faultType],
          alert: {
            ...currentSettings[faultType]?.alert,
            hours: 0,
          },
        },
      };
      onSettingChange(newSettings);
      return;
    }

    const newHours = shouldBeNegative ? -Math.abs(parsedHours) : Math.abs(parsedHours);

    const newSettings = {
      ...currentSettings,
      [faultType]: {
        ...currentSettings[faultType],
        alert: {
          ...currentSettings[faultType]?.alert,
          hours: newHours,
        },
      },
    };
    onSettingChange(newSettings);
  };

  const handleReminderToggle = (faultType) => {
    if (disabled) return;

    const newSettings = {
      ...currentSettings,
      [faultType]: {
        ...currentSettings[faultType],
        reminders: {
          ...currentSettings[faultType].reminders,
          enabled: !currentSettings[faultType]?.reminders?.enabled,
        },
      },
    };
    onSettingChange(newSettings);
  };

  const handleReminderHoursChange = (faultType, hours) => {
    if (disabled) return;

    // Allow empty string during editing - set to null so input can be cleared
    if (hours === '' || hours === null || hours === undefined) {
      const newSettings = {
        ...currentSettings,
        [faultType]: {
          ...currentSettings[faultType],
          reminders: {
            ...currentSettings[faultType].reminders,
            hours: 0,
          },
        },
      };
      onSettingChange(newSettings);
      return;
    }

    const parsedHours = parseFloat(hours);
    if (isNaN(parsedHours)) return;

    const newSettings = {
      ...currentSettings,
      [faultType]: {
        ...currentSettings[faultType],
        reminders: {
          ...currentSettings[faultType].reminders,
          hours: parsedHours,
        },
      },
    };
    onSettingChange(newSettings);
  };

  const handleReminderHoursBlur = (faultType, hours) => {
    if (disabled) return;

    // Convert emptyish values to 0 on blur
    const trimmedHours = typeof hours === 'string' ? hours.trim() : hours;
    if (trimmedHours === '' || trimmedHours === null || trimmedHours === undefined) {
      const newSettings = {
        ...currentSettings,
        [faultType]: {
          ...currentSettings[faultType],
          reminders: {
            ...currentSettings[faultType].reminders,
            hours: 0.5,
          },
        },
      };
      onSettingChange(newSettings);
      return;
    }

    const parsedHours = parseFloat(trimmedHours);
    const finalHours = isNaN(parsedHours) ? 0 : parsedHours;

    const newSettings = {
      ...currentSettings,
      [faultType]: {
        ...currentSettings[faultType],
        reminders: {
          ...currentSettings[faultType].reminders,
          hours: finalHours,
        },
      },
    };
    onSettingChange(newSettings);
  };

  const handleResolvedToggle = (faultType) => {
    if (disabled) return;

    const newSettings = {
      ...currentSettings,
      [faultType]: {
        ...currentSettings[faultType],
        resolved: {
          enabled: !currentSettings[faultType]?.resolved?.enabled,
        },
      },
    };
    onSettingChange(newSettings);
  };

  if (disabled) {
    return <TierSettingsSummary tierName={tierName} settings={currentSettings} showTitle={showTitle} />;
  }

  return (
    <div className="TierSettingsPanel">
      {showTitle && <Heading className="TierSettingsPanel-title">{tierName}</Heading>}

      <div className="TierSettingsPanel-content">
        <div className="TierSettingsPanel-filtersContainer">
          {currentSettings &&
            Object.entries(currentSettings).map(([faultType, faultSettings]) => {
              const faultName = getTierFaultName(parseInt(faultType));
              const alertSettings = faultSettings.alert || {};
              const isEnabled = alertSettings.enabled;
              const reminderSettings = faultSettings.reminders || {};
              const isReminderEnabled = reminderSettings.enabled || false;
              const resolvedSettings = faultSettings.resolved || {};
              const isResolvedEnabled = resolvedSettings.enabled || false;
              const faultCode = parseInt(faultType);
              const isImmediateOneShot = isImmediateOneShotAlert(faultCode);

              const alertType =
                alertSettings.type === 'nofeed_time' && alertSettings.hours < 0
                  ? 'before_empty'
                  : alertSettings.type === 'nofeed_time' && alertSettings.hours >= 0
                  ? 'after_empty'
                  : 'after_duration';

              const isNoFeedAlert = ALERTS_WITH_NO_FEED_TIME.includes(faultCode);

              const durationTimingOption = {
                id: 'after_duration',
                name: getEditorDurationTimingLabel(faultCode),
              };

              const alertOptions = isNoFeedAlert
                ? [durationTimingOption, ...NO_FEED_ALERT_OPTIONS]
                : [durationTimingOption];

              const hasHoursError =
                isEnabled &&
                !isImmediateOneShot &&
                alertSettings.type === 'duration' &&
                (alertSettings.hours === null || alertSettings.hours === undefined || alertSettings.hours === '');

              const hasReminderError =
                isEnabled &&
                !isImmediateOneShot &&
                isReminderEnabled &&
                (reminderSettings.hours === 0 ||
                  reminderSettings.hours === null ||
                  reminderSettings.hours === undefined);

              return (
                <div
                  key={faultType}
                  className={`TierSettingsPanel-filterRow${
                    isImmediateOneShot ? ' TierSettingsPanel-filterRow--immediate-one-shot' : ''
                  }`}
                >
                  <ToggleSwitch
                    isActive={isEnabled}
                    onToggle={() => handleToggle(faultType)}
                    disabled={disabled}
                    className="TierSettingsPanel-enableToggle"
                    aria-label={`Enable ${faultName} alerts`}
                  />
                  <span
                    className={`TierSettingsPanel-faultName ${
                      disabled && !isEnabled ? 'TierSettingsPanel-strikethrough' : ''
                    }`}
                  >
                    {faultName}
                  </span>
                  <div className="TierSettingsPanel-timingSection">
                    {isImmediateOneShot ? (
                      <span
                        className={`TierSettingsPanel-immediateLabel ${
                          disabled && !isEnabled ? 'TierSettingsPanel-strikethrough' : ''
                        }`}
                      >
                        Immediately
                      </span>
                    ) : (
                      <>
                        <div
                          className={`TierSettingsPanel-hoursWrapper ${
                            hasHoursError ? 'TierSettingsPanel-hoursWrapper--error' : ''
                          }`}
                        >
                          <NumberInput
                            name={`${faultType}_hours`}
                            value={
                              alertSettings.hours !== '' &&
                              alertSettings.hours !== null &&
                              alertSettings.hours !== undefined &&
                              !isNaN(Number(alertSettings.hours))
                                ? Math.abs(alertSettings.hours)
                                : ''
                            }
                            onChange={(e) => handleHoursChange(faultType, e.target.value)}
                            onBlur={(e) => handleHoursBlur(faultType, e.target.value)}
                            min={0}
                            max={120}
                            step={0.5}
                            disabled={!isEnabled || disabled}
                            className="TierSettingsPanel-hoursInput"
                            isValid={!hasHoursError}
                            error={hasHoursError ? 'Cannot be blank' : ''}
                          />
                          <span
                            className={`TierSettingsPanel-hoursLabel ${
                              disabled && !isEnabled ? 'TierSettingsPanel-strikethrough' : ''
                            } ${hasHoursError ? 'TierSettingsPanel-hoursLabel--error' : ''}`}
                          >
                            hours
                          </span>
                        </div>
                        <div className="TierSettingsPanel-typeSelectPlaceholder">
                          {isNoFeedAlert ? (
                            <Select
                              name={`${faultType}_type`}
                              value={alertType}
                              onChange={(e) => handleTypeChange(faultType, e.target.value)}
                              itemList={alertOptions.map((option) => ({
                                id: option.id,
                                name: option.name,
                              }))}
                              displayOnly={!isEnabled || disabled}
                              className={`TierSettingsPanel-timeTypeSelect ${
                                disabled && !isEnabled ? 'TierSettingsPanel-strikethrough' : ''
                              }`}
                            />
                          ) : (
                            <span
                              className={`TierSettingsPanel-timingTypeLabel ${
                                disabled && !isEnabled ? 'TierSettingsPanel-strikethrough' : ''
                              }`}
                            >
                              {getEditorDurationTimingLabel(faultCode)}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {!isImmediateOneShot && (
                    <>
                      <div className="TierSettingsPanel-reminderSection">
                        <ToggleSwitch
                          isActive={isReminderEnabled}
                          onToggle={() => handleReminderToggle(faultType)}
                          disabled={!isEnabled || disabled}
                          className="TierSettingsPanel-reminderToggle"
                          aria-label={`Enable ${faultName} reminders`}
                        />
                        <span
                          className={`TierSettingsPanel-reminderLabel ${
                            disabled && (!isReminderEnabled || !isEnabled) ? 'TierSettingsPanel-strikethrough' : ''
                          }`}
                        >
                          Remind
                        </span>
                        <NumberInput
                          name={`${faultType}_reminder_hours`}
                          value={
                            alertSettings.hours !== '' &&
                            reminderSettings.hours !== null &&
                            reminderSettings.hours !== undefined &&
                            !isNaN(Number(reminderSettings.hours)) &&
                            reminderSettings.hours !== 0
                              ? reminderSettings.hours
                              : ''
                          }
                          onChange={(e) => handleReminderHoursChange(faultType, e.target.value)}
                          onBlur={(e) => handleReminderHoursBlur(faultType, e.target.value)}
                          min={0}
                          max={120}
                          step={0.5}
                          disabled={!isEnabled || !isReminderEnabled || disabled}
                          className={`TierSettingsPanel-reminderHoursInput ${
                            hasReminderError ? 'TierSettingsPanel-reminderHoursInput--error' : ''
                          }`}
                          isValid={!hasReminderError}
                          error={hasReminderError ? 'Cannot be blank' : ''}
                        />
                        <span
                          className={`TierSettingsPanel-reminderHoursLabel ${
                            hasReminderError ? 'TierSettingsPanel-reminderHoursLabel--error' : ''
                          } ${disabled && (!isReminderEnabled || !isEnabled) ? 'TierSettingsPanel-strikethrough' : ''} `}
                        >
                          hours
                        </span>
                      </div>
                      <div className="TierSettingsPanel-resolvedSection">
                        <ToggleSwitch
                          isActive={isResolvedEnabled}
                          onToggle={() => handleResolvedToggle(faultType)}
                          disabled={!isEnabled || disabled}
                          className="TierSettingsPanel-resolvedToggle"
                          aria-label={`Enable ${faultName} resolved notifications`}
                        />
                        <span
                          className={`TierSettingsPanel-resolvedLabel ${
                            disabled && (!isResolvedEnabled || !isEnabled) ? 'TierSettingsPanel-strikethrough' : ''
                          }`}
                        >
                          Resolved
                        </span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

TierSettingsPanel.propTypes = {
  tierName: PropTypes.string,
  settings: PropTypes.object,
  onSettingChange: PropTypes.func,
  disabled: PropTypes.bool,
  showTitle: PropTypes.bool,
};

export default TierSettingsPanel;
